// src/components/admin/SessionsTab.jsx
// Sessions actives de l’administrateur connecté + revoke-admin (Super Admin).

import React, { useCallback, useEffect, useState } from 'react';
import {
  RefreshCw,
  ShieldAlert,
  CheckCircle2,
  Monitor,
  Trash2,
  LogOut,
  AlertTriangle,
} from 'lucide-react';
import { EmptyState } from '../ui/UIComponents';
import {
  getSessions,
  revokeSession,
  revokeOtherSessions,
  revokeAdminSessions,
} from '../../services/adminSessions';
import { authService } from '../../services/authService';

function formatDate(value) {
  if (value === undefined || value === null || value === '') return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString('fr-FR');
  } catch {
    return String(value);
  }
}

function userErrorMessage(err) {
  const raw =
    (typeof err?.body?.error === 'string' && err.body.error) ||
    (typeof err?.body?.erreur === 'string' && err.body.erreur) ||
    err?.message ||
    '';
  const key = String(raw).trim();
  if (err?.status === 401 || /non authentifié/i.test(key)) {
    return 'Session expirée. Veuillez vous reconnecter.';
  }
  if (err?.status === 403 || /accès refusé/i.test(key)) {
    return 'Accès refusé. Vous n’avez pas les droits nécessaires.';
  }
  if (err?.status === 404 || /session introuvable/i.test(key)) {
    return 'Session introuvable.';
  }
  if (/session_id requis/i.test(key)) return 'Identifiant de session manquant.';
  if (/administrateur_id requis/i.test(key)) return 'Identifiant administrateur manquant.';
  if (/corps de requête invalide/i.test(key)) return 'Requête invalide.';
  return key || 'Une erreur est survenue.';
}

/** Super Admin : niveau code SUPER_ADMIN ou id/code 1 (contrat Worker). */
function isSuperAdminUser() {
  const user = authService.getCurrentUser();
  if (!user) return false;
  const n = user.niveau;
  if (n === 1 || n === '1') return true;
  if (typeof n === 'string' && n.toUpperCase() === 'SUPER_ADMIN') return true;
  return false;
}

export default function SessionsTab({ admins = [] }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [confirmRevoke, setConfirmRevoke] = useState(null); // session object
  const [confirmOthers, setConfirmOthers] = useState(false);
  const [confirmAdminId, setConfirmAdminId] = useState(null);
  const [targetAdminId, setTargetAdminId] = useState('');

  const superAdmin = isSuperAdminUser();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSessions();
      const list = Array.isArray(res?.sessions) ? res.sessions : [];
      setSessions(list);
    } catch (err) {
      setSessions([]);
      setError(userErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runRevokeOne = async () => {
    if (!confirmRevoke?.id) return;
    setActionLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await revokeSession(confirmRevoke.id);
      setConfirmRevoke(null);
      if (confirmRevoke.session_courante) {
        setSuccess('Session courante révoquée. Vous serez peut-être invité à vous reconnecter.');
      } else {
        setSuccess('Session révoquée.');
      }
      await load();
    } catch (err) {
      setError(userErrorMessage(err));
      setConfirmRevoke(null);
    } finally {
      setActionLoading(false);
    }
  };

  const runRevokeOthers = async () => {
    setConfirmOthers(false);
    setActionLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await revokeOtherSessions();
      const n = typeof res?.sessions_revoquees === 'number' ? res.sessions_revoquees : 0;
      setSuccess(
        n === 0
          ? 'Aucune autre session à révoquer.'
          : `${n} autre${n > 1 ? 's' : ''} session${n > 1 ? 's' : ''} révoquée${n > 1 ? 's' : ''}.`
      );
      await load();
    } catch (err) {
      setError(userErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const runRevokeAdmin = async () => {
    if (!confirmAdminId) return;
    setActionLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await revokeAdminSessions(confirmAdminId);
      const n = typeof res?.sessions_revoquees === 'number' ? res.sessions_revoquees : 0;
      setSuccess(
        n === 0
          ? 'Aucune session active pour cet administrateur.'
          : `${n} session${n > 1 ? 's' : ''} révoquée${n > 1 ? 's' : ''} pour l’administrateur ciblé.`
      );
      setConfirmAdminId(null);
      await load();
    } catch (err) {
      setError(userErrorMessage(err));
      setConfirmAdminId(null);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl text-rose-800 text-xs font-bold flex justify-between items-center gap-2">
          <span className="flex items-center gap-2">
            <ShieldAlert size={16} /> {error}
          </span>
          <button type="button" onClick={load} className="dsi-btn-secondary text-xs shrink-0">
            Réessayer
          </button>
        </div>
      )}
      {success && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-emerald-800 text-xs font-bold flex justify-between items-center gap-2">
          <span className="flex items-center gap-2">
            <CheckCircle2 size={16} /> {success}
          </span>
          <button type="button" onClick={() => setSuccess(null)} className="underline text-[11px]">
            Fermer
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <Monitor size={16} className="text-indigo-600" /> Mes sessions actives
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {loading ? '…' : `${sessions.length} session${sessions.length !== 1 ? 's' : ''} active${sessions.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="dsi-btn-secondary text-xs" onClick={load} disabled={loading || actionLoading}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Actualiser
          </button>
          <button
            type="button"
            className="dsi-btn-secondary text-xs"
            disabled={loading || actionLoading || sessions.length <= 1}
            onClick={() => setConfirmOthers(true)}
          >
            <LogOut size={14} /> Révoquer les autres
          </button>
        </div>
      </div>

      {loading ? (
        <div className="dsi-card p-12 text-center text-xs text-slate-500">Chargement des sessions…</div>
      ) : (
        <div className="dsi-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 font-semibold border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3">Session</th>
                  <th className="px-4 py-3">IP</th>
                  <th className="px-4 py-3">Navigateur</th>
                  <th className="px-4 py-3">Créée le</th>
                  <th className="px-4 py-3">Expire le</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sessions.map((s) => (
                  <tr key={s.id} className={s.session_courante ? 'bg-indigo-50/40' : 'hover:bg-slate-50'}>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <span className="font-mono text-[11px] text-slate-700 break-all">{s.id}</span>
                        {s.session_courante && (
                          <span className="inline-flex w-fit items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-100 text-indigo-700 border border-indigo-200">
                            Session courante
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">{s.ip_creation != null && s.ip_creation !== '' ? s.ip_creation : '—'}</td>
                    <td className="px-4 py-3 text-xs text-slate-600 max-w-[220px] truncate" title={s.user_agent || ''}>
                      {s.user_agent != null && s.user_agent !== '' ? s.user_agent : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{formatDate(s.created_at)}</td>
                    <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{formatDate(s.expire_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        className="dsi-btn-ghost text-xs text-rose-700"
                        disabled={actionLoading}
                        onClick={() => setConfirmRevoke(s)}
                      >
                        <Trash2 size={14} /> Révoquer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!sessions.length && (
            <EmptyState title="Aucune session active" message="Aucune session active n’a été trouvée pour votre compte." />
          )}
        </div>
      )}

      {superAdmin && (
        <div className="dsi-card p-5 space-y-3 border border-amber-200/80 bg-amber-50/30">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-amber-700" />
            <h3 className="text-sm font-bold text-slate-900">Révoquer les sessions d’un administrateur</h3>
          </div>
          <p className="text-xs text-slate-600">
            Action réservée au Super Admin. Toutes les sessions actives du compte ciblé seront invalidées.
          </p>
          <div className="flex flex-wrap gap-2 items-end">
            <label className="block space-y-1 flex-1 min-w-[200px]">
              <span className="text-[11px] font-semibold text-slate-600">Administrateur</span>
              <select
                className="dsi-input"
                value={targetAdminId}
                onChange={(e) => setTargetAdminId(e.target.value)}
                disabled={actionLoading}
              >
                <option value="">Sélectionner…</option>
                {admins.map((a) => (
                  <option key={a.id} value={String(a.id)}>
                    {(a.nom_complet || a.email || `Admin #${a.id}`) + (a.email ? ` (${a.email})` : '')}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="dsi-btn-primary text-xs"
              disabled={!targetAdminId || actionLoading}
              onClick={() => setConfirmAdminId(targetAdminId)}
            >
              Révoquer les sessions
            </button>
          </div>
        </div>
      )}

      {/* Confirm revoke one */}
      {confirmRevoke && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => !actionLoading && setConfirmRevoke(null)} />
          <div className="relative w-full max-w-md dsi-card p-6 space-y-4">
            <h3 className="font-bold text-slate-900">Révoquer cette session ?</h3>
            <p className="text-xs text-slate-600">
              {confirmRevoke.session_courante
                ? 'Il s’agit de votre session courante. Après révocation, vous pourrez être invité à vous reconnecter.'
                : 'La session sélectionnée sera invalidée immédiatement.'}
            </p>
            <p className="font-mono text-[11px] text-slate-500 break-all">{confirmRevoke.id}</p>
            <div className="flex justify-end gap-2">
              <button type="button" className="dsi-btn-secondary text-xs" disabled={actionLoading} onClick={() => setConfirmRevoke(null)}>
                Annuler
              </button>
              <button type="button" className="dsi-btn-primary text-xs" disabled={actionLoading} onClick={runRevokeOne}>
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmOthers && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setConfirmOthers(false)} />
          <div className="relative w-full max-w-md dsi-card p-6 space-y-4">
            <h3 className="font-bold text-slate-900">Révoquer toutes les autres sessions ?</h3>
            <p className="text-xs text-slate-600">
              Toutes vos sessions actives seront invalidées, à l’exception de la session en cours.
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" className="dsi-btn-secondary text-xs" onClick={() => setConfirmOthers(false)}>
                Annuler
              </button>
              <button type="button" className="dsi-btn-primary text-xs" onClick={runRevokeOthers}>
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmAdminId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => !actionLoading && setConfirmAdminId(null)} />
          <div className="relative w-full max-w-md dsi-card p-6 space-y-4">
            <h3 className="font-bold text-slate-900">Révoquer les sessions de cet administrateur ?</h3>
            <p className="text-xs text-slate-600">
              Toutes les sessions actives du compte sélectionné seront invalidées.
            </p>
            <p className="font-mono text-[11px] text-slate-500">ID : {confirmAdminId}</p>
            <div className="flex justify-end gap-2">
              <button type="button" className="dsi-btn-secondary text-xs" disabled={actionLoading} onClick={() => setConfirmAdminId(null)}>
                Annuler
              </button>
              <button type="button" className="dsi-btn-primary text-xs" disabled={actionLoading} onClick={runRevokeAdmin}>
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
